import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Question, PhoneCall, Note, UsersThree, Buildings, Flask,
  ShieldWarning, Sparkle, Wrench,
} from "@phosphor-icons/react";

// Reference: Pulse Notes.xlsx — SOP for logging Notes & Calls.
// Excel said "Firm" everywhere; product now calls that "Live firm".
const NOTE_CARDS = [
  {
    title: "Live firm",
    icon: Buildings,
    accent: "violet",
    when: "You're working on a paying customer's setup or feedback.",
    examples: [
      "Validating their TB Excel import or Ledger Scrutiny Rules on the test/support site",
      "Capturing customer feedback, suggestions or observations",
      "Analysing a specific firm's request before responding",
    ],
  },
  {
    title: "Trial firm",
    icon: Flask,
    accent: "emerald",
    when: "Same work, but the firm is still on trial.",
    examples: [
      "Testing a trial firm's TB import / Ledger Scrutiny Rules",
      "Logging onboarding-time feedback or observations",
    ],
  },
  {
    title: "General",
    icon: Sparkle,
    accent: "amber",
    when: "Internal AssureAI work that isn't tied to a specific firm.",
    examples: [
      "Exploring or testing AssureAI features (existing or new)",
      "Release testing and QA cycles",
      "Product walkthroughs for self-learning",
    ],
  },
];

const CALL_CARDS = [
  {
    title: "Live firm · Proactive",
    icon: ShieldWarning,
    accent: "rose",
    when: "You're reaching out because the firm looks at risk.",
    examples: [
      "Low-usage outreach",
      "At-risk or potential-churn check-in",
      "Win-back attempts before renewal",
    ],
  },
  {
    title: "Live firm · Support",
    icon: Wrench,
    accent: "violet",
    when: "Customer-initiated help across any channel.",
    examples: [
      "Technical / process queries via chat, call or meeting",
      "Sales, marketing or audit questions",
      "Resolving an issue raised by the firm",
    ],
  },
  {
    title: "Live firm · Other",
    icon: PhoneCall,
    accent: "sky",
    when: "Business conversations with a live firm that aren't support.",
    examples: [
      "New developments, renewals, feedback surveys",
      "Discussions with founders / partners / key people",
      "Product improvement and roadmap chats",
    ],
  },
  {
    title: "Trial firm",
    icon: Flask,
    accent: "emerald",
    when: "Any call with a firm that's still on trial.",
    examples: [
      "Onboarding walkthroughs",
      "Follow-ups, demos, query resolution",
      "Trial-conversion conversations",
    ],
  },
  {
    title: "Internal",
    icon: UsersThree,
    accent: "slate",
    when: "Time spent talking to your own team.",
    examples: [
      "Daily stand-ups and team meetings",
      "1:1s or cross-team syncs",
      "Internal product / process discussions",
    ],
  },
];

const ACCENT = {
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  slate: "bg-slate-100 border-slate-300 text-slate-700",
};

function Card({ item }) {
  const Icon = item.icon;
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-violet-300 transition-colors" data-testid={`howto-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start gap-2.5">
        <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border ${ACCENT[item.accent]}`}>
          <Icon size={15} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[13px] text-gray-900 leading-tight">{item.title}</div>
          <div className="text-[12px] text-gray-600 mt-0.5 leading-snug">{item.when}</div>
        </div>
      </div>
      <ul className="mt-2.5 pl-1 space-y-1">
        {item.examples.map((ex, i) => (
          <li key={i} className="text-[12px] text-gray-700 flex gap-2 leading-snug">
            <span className="text-gray-300 shrink-0">›</span>
            <span>{ex}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ActivityHowToUse() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-8 px-2 text-gray-500 hover:text-violet-700 hover:bg-violet-50 gap-1.5"
        data-testid="act-howto-open"
      >
        <Question size={15} weight="bold" />
        <span className="text-xs font-medium">How to use</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="sm:max-w-md w-full overflow-y-auto p-0"
          data-testid="act-howto-drawer"
        >
          <div className="p-6 pb-3 border-b border-gray-100 bg-gradient-to-b from-violet-50/40 to-white">
            <SheetHeader>
              <SheetTitle className="font-display text-xl tracking-tight">How to log activity</SheetTitle>
              <SheetDescription className="text-[13px] text-gray-600">
                Pick the right bucket so your team can slice the data later. Use this as a quick reference — every entry helps the CS team spot patterns, risk and wins.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="p-6 pt-4">
            <Tabs defaultValue="notes" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4" data-testid="act-howto-tabs">
                <TabsTrigger value="notes" data-testid="act-howto-tab-notes" className="gap-1.5">
                  <Note size={13} weight="fill" /> Notes
                </TabsTrigger>
                <TabsTrigger value="calls" data-testid="act-howto-tab-calls" className="gap-1.5">
                  <PhoneCall size={13} weight="fill" /> Calls
                </TabsTrigger>
              </TabsList>

              <TabsContent value="notes" className="space-y-3 mt-0">
                <p className="text-[12px] text-gray-500 leading-snug">
                  A <span className="font-semibold text-gray-700">Note</span> captures work you did
                  {" "}<em>without</em> a phone call — testing, analysis, feedback you wrote down.
                  Log the time you spent so the team sees real work hours.
                </p>
                {NOTE_CARDS.map((c) => <Card key={c.title} item={c} />)}
              </TabsContent>

              <TabsContent value="calls" className="space-y-3 mt-0">
                <p className="text-[12px] text-gray-500 leading-snug">
                  A <span className="font-semibold text-gray-700">Call</span> covers any spoken conversation — phone, IVR, meeting or video.
                  Always log the <span className="font-semibold text-gray-700">exact duration</span> (talk-time), even for short calls.
                </p>
                {CALL_CARDS.map((c) => <Card key={c.title} item={c} />)}

                <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 leading-snug">
                  <span className="font-semibold">Tip:</span> Customer calls from IVR (or any other source) should be logged with the exact duration shown in the source — not rounded.
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
